import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'
import { Lock } from 'lucide-react'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const checkedRef = useRef(false)

  useEffect(() => {
    // 防重：StrictMode 下 effect 双跑，避免重复请求认证接口
    if (!checkedRef.current) {
      checkedRef.current = true
      checkAuth()
    }
  }, [checkAuth])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="space-y-4 w-full max-w-md">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // 不自动跳转，而是显示提示信息
    return (
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Card className="shadow-lg border-0">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-orange-500" />
            </div>
            <CardTitle className="text-xl">需要登录</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              此功能需要登录后才能使用，请先登录或注册账号
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                onClick={() => navigate('/login', { state: { from: location } })}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                去登录
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate(-1)}
              >
                返回
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}
